import { browser } from "$app/environment";

export type ToastVariant = "info" | "success" | "warning" | "danger";

export interface ToastItem {
  id: number;
  variant: ToastVariant;
  title: string;
  description: string | null;
  durationMs: number;
  dedupeKey: string;
  count: number;
  persistent: boolean;
}

const MAX_VISIBLE = 3;

const DEFAULT_DURATION: Record<ToastVariant, number> = {
  info: 4000,
  success: 4000,
  warning: 5000,
  danger: 5000,
};

let items = $state<ToastItem[]>([]);
let nextId = 1;
const timers = new Map<number, ReturnType<typeof setTimeout>>();

function defaultDedupeKey(
  variant: ToastVariant,
  title: string,
  description: string | null,
): string {
  return `${variant}::${title}::${description ?? ""}`;
}

function scheduleDismiss(id: number, durationMs: number): void {
  if (!browser) return;
  const existing = timers.get(id);
  if (existing) clearTimeout(existing);
  if (durationMs <= 0) return;
  timers.set(
    id,
    setTimeout(() => toast.dismiss(id), durationMs),
  );
}

function clearTimer(id: number): void {
  const timer = timers.get(id);
  if (timer) {
    clearTimeout(timer);
    timers.delete(id);
  }
}

function enforceMaxStack(): void {
  while (items.length > MAX_VISIBLE) {
    const oldest = items[0];
    if (!oldest) break;
    toast.dismiss(oldest.id);
  }
}

export const toast = {
  get items(): ToastItem[] {
    return items;
  },
  show(options: {
    variant?: ToastVariant;
    title: string;
    description?: string | null;
    durationMs?: number;
    dedupeKey?: string;
    persistent?: boolean;
  }): number {
    const variant = options.variant ?? "info";
    const description = options.description ?? null;
    const persistent = options.persistent === true;
    const durationMs = persistent
      ? 0
      : (options.durationMs ?? DEFAULT_DURATION[variant]);
    const dedupeKey =
      options.dedupeKey ?? defaultDedupeKey(variant, options.title, description);

    const existing = items.find((item) => item.dedupeKey === dedupeKey);
    if (existing) {
      items = items.map((item) =>
        item.id === existing.id
          ? {
              ...item,
              title: options.title,
              description,
              durationMs,
              persistent,
              count: item.count + 1,
            }
          : item,
      );
      if (!persistent) scheduleDismiss(existing.id, durationMs);
      return existing.id;
    }

    const id = nextId++;
    items = [
      ...items,
      {
        id,
        variant,
        title: options.title,
        description,
        durationMs,
        dedupeKey,
        count: 1,
        persistent,
      },
    ];
    if (!persistent) scheduleDismiss(id, durationMs);
    enforceMaxStack();
    return id;
  },
  success(title: string, description?: string | null): number {
    return toast.show({ variant: "success", title, description });
  },
  error(title: string, description?: string | null): number {
    return toast.show({ variant: "danger", title, description });
  },
  dismiss(id: number): void {
    items = items.filter((item) => item.id !== id);
    clearTimer(id);
  },
};
